import { computed, useContext } from '@nuxtjs/composition-api'
import { useLoading } from '../ui/useLoading'
import { useCustomer } from '../useCustomer'
import {
  CustomerAddress,
  CustomerAddressType
} from '~/types/customer'
import project from '~/project.config.js'

export const MAX_DELIVERY_ADDRESSES_QUANTITY = 4

const loading = useLoading()

export const useAddress = () => {

  const { app } = useContext()
  const {
    customer,
    ensureCustomer,
    getCustomer,
  } = useCustomer()

  const addresses = computed(() => customer.value?.addresses ?? [])

  const addressOfType = (addrType: CustomerAddressType) => {
    return addresses.value.find((addr) => addr.type === addrType)
  }

  const defaultAddress = computed(() =>
    addressOfType(CustomerAddressType.Default)
  )
  const deliveryAddress = computed(() =>
    addressOfType(CustomerAddressType.Delivery)
  )
  const invoiceAddress = computed(() =>
    addressOfType(CustomerAddressType.Invoice)
  )
  const deliveryAddresses = computed(() => {
    return addresses.value.filter(
      (addr) => (addr.type?.indexOf('Delivery') ?? -1) >= 0
    )
  })

  const otherDeliveryAddresses = computed(() => {
    return deliveryAddresses.value.filter(
      (address) => address.type !== CustomerAddressType.Delivery
    )
  })

  const otherAddressAddible = computed(() =>
    otherDeliveryAddresses.value.length < MAX_DELIVERY_ADDRESSES_QUANTITY
  )

  const logisticAddress = computed(() => {
    const contactCentric = project.customer?.contactCentric ?? false
    if (contactCentric) {
      return customer.value?.contactAddress ?? null
    } else {
      return addressOfType(CustomerAddressType.Delivery)
    }
  })

  const nextDeliveryAddressType = (list: CustomerAddress[], type: string) => {
    const prefix = type + 'Address'
    const typesSet = new Set(
      list.reduce((prev, current) => {
        if (current.type) {
          const index =
            current.type === type ? 0 : Number(current.type.replace(prefix, ''))
          if (!isNaN(index)) {
            prev.push(index)
          }
        }
        return prev
      }, [] as number[])
    )

    let i = 0
    while (typesSet.has(i)) i++

    return i > 0 ? prefix + i : type
  }

  const ensureAddresses = async () => {
    await ensureCustomer()
  }

  const refreshAddresses = async () => {
    await getCustomer()
  }

  const addDeliveryAddress = (address: CustomerAddress) => {
    address.type = nextDeliveryAddressType(
      deliveryAddresses.value,
      CustomerAddressType.Delivery
    )
    return loading.scope(async () => {
      const addressId = await app.$api.customerAddress.addAddress(address)
      if (addressId === null) return false

      address.id = addressId
      addresses.value.push(address)

      return true
    })
  }

  const removeDeliveryAddress = async (addressId: number) => {
    const success = await app.$api.customerAddress.removeAddress(addressId)
    if (success) {
      const index = addresses.value.findIndex(addr => addr.id === addressId)
      if (index >= 0) {
        addresses.value.splice(index, 1)
      }
    }
    return success
  }

  const setDefaultAddress = async (address: CustomerAddress) => {
    if (!customer.value) return

    const token = await app.$api.customer.updateCustomer({
      id: customer.value.id,
      email: customer.value.email,
      firstName: customer.value.firstName,
      addresses: [address]
    })
    if (token) {
      await getCustomer()
    }
  }

  const setInvoiceAddress = async (address: CustomerAddress) => {
    address.type = CustomerAddressType.Invoice
    const result = await loading.scope(async () => {
      if (invoiceAddress.value) {
        const tryAddress = Object.assign({}, invoiceAddress.value, address)
        const success = await updateAddress(tryAddress)
        if (!success) return false

        Object.assign(invoiceAddress.value, address)
        return true
      } else {
        const addressId = await app.$api.customerAddress.addAddress(address)
        if (addressId === null) return false

        address.id = addressId
        addresses.value.push(address)
        return true
      }
    })

    return result ?? false
  }

  const updateAddress = async (address: CustomerAddress) => {
    const success = await app.$api.customerAddress.updateAddress(address)
    if (success) {
      const index = addresses.value.findIndex(addr => addr.id === address.id)
      if (index >= 0) {
        addresses.value.splice(index, 1, address)
      }
    }
    return success
  }

  return {
    defaultAddress,
    deliveryAddress,
    deliveryAddresses,
    otherDeliveryAddresses,
    otherAddressAddible,
    invoiceAddress,
    logisticAddress,

    loading,

    ensureAddresses,
    refreshAddresses,
    addDeliveryAddress,
    removeDeliveryAddress,
    setDefaultAddress,
    setInvoiceAddress,
    updateAddress
  }
}
